import React, { useEffect, useRef, useState, useCallback } from 'react';
import { convertHeicIfNeeded } from '../utils/convertHeic';
import {
	Paper,
	Box,
	Typography,
	IconButton,
	Avatar,
	InputBase,
	CircularProgress,
	Portal,
} from '@mui/material';
import {
	Close as CloseIcon,
	Send as SendIcon,
	AttachFile as AttachFileIcon,
	Image as ImageIcon,
	Reply as ReplyIcon,
} from '@mui/icons-material';

import ImagePreviewModal from './ImagePreviewModal';
import { chatService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { joinConversation, leaveConversation } from '../config/socket';

const CHAT_API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';
const CHAT_HEADER_IMG = 'https://images.unsplash.com/photo-1453747063559-36695c8771bd?w=1920&q=80';
const getAvatarUrl = (avatar) => {
	if (!avatar) return undefined;
	const p = typeof avatar === 'object' ? avatar.path : avatar;
	return p ? `${ CHAT_API_BASE }${ p }` : undefined;
};

const lightenColor = (hex, amt = 0.82) => {
	if (!hex || !hex.startsWith('#') || hex.length < 7) return '#e9e9eb';
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgb(${ Math.round(r + (255 - r) * amt) },${ Math.round(g + (255 - g) * amt) },${ Math.round(b + (255 - b) * amt) })`;
};

const formatTime = (date) => {
	const d = new Date(date);
	return `${ String(d.getHours()).padStart(2, '0') }:${ String(d.getMinutes()).padStart(2, '0') }`;
};

const formatDateLabel = (date) => {
	const d = new Date(date);
	const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
	return `${ days[d.getDay()] } ${ String(d.getDate()).padStart(2, '0') }/${ String(d.getMonth() + 1).padStart(2, '0') }/${ d.getFullYear() }`;
};

/**
 * Cửa sổ chat nổi cho phía user — giao diện tương tự admin nhưng không có
 * recall / delete / theme editor (user không có quyền theo phía BE).
 *
 * Props:
 * - conversation: object (đã được tạo qua chatService.getOrCreateDirect)
 * - onClose: () => void
 */
const FloatingChatWindow = ({ conversation, onClose }) => {
	const { user } = useAuth();

	useEffect(() => {
		window.dispatchEvent(new CustomEvent('floatingChatToggle', { detail: true }));
		// Lock body scroll on mobile to prevent scroll bleed-through
		const isMobile = window.innerWidth < 600;
		let savedScrollY = 0;
		if (isMobile) {
			savedScrollY = window.scrollY;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${savedScrollY}px`;
			document.body.style.width = '100%';
			document.body.style.overflow = 'hidden';
		}
		return () => {
			window.dispatchEvent(new CustomEvent('floatingChatToggle', { detail: false }));
			if (isMobile) {
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.width = '';
				document.body.style.overflow = '';
				window.scrollTo(0, savedScrollY);
			}
		};
	}, []);
	const { socket } = useSocket();

	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const [sending, setSending] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [replyingTo, setReplyingTo] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);

	const scrollRef = useRef(null);
	const endRef = useRef(null);
	const fileInputRef = useRef(null);
	const imageInputRef = useRef(null);
	const getSelectedFile = () => imageInputRef.current?.files?.[0] || fileInputRef.current?.files?.[0];
	const shouldScrollToBottom = useRef(true);
	const processedMsgIds = useRef(new Set());

	const currentUserId = (user?.id || user?._id)?.toString();
	const other = conversation?.participants?.find(
		(p) => p._id?.toString() !== currentUserId
	);

	// Theme từ DB (read-only ở phía user)
	const ownBubbleColor = conversation?.bubbleColor || '#D4AF37';
	const otherBubbleColor = conversation?.bubbleColor ? lightenColor(conversation.bubbleColor, 0.82) : '#1e1f2a';
	const datePillColor = conversation?.bubbleColor ? lightenColor(conversation.bubbleColor, 0.72) : 'rgba(255,255,255,0.08)';
	const bgValue = conversation?.theme;
	const chatBgColor = bgValue
		? bgValue.startsWith('/')
			? `url(${ CHAT_API_BASE }${ bgValue }) center/cover no-repeat`
			: bgValue
		: '#0e0f17';

	// Tải messages ban đầu + join room
	useEffect(() => {
		if (!conversation?._id) return;
		joinConversation(conversation._id);
		let cancel = false;
		(async () => {
			try {
				const res = await chatService.getMessages(conversation._id, { limit: 30 });
				if (cancel) return;
				const list = res.data || [];
				setMessages(list);
				setHasMore(res.hasMore || false);
				shouldScrollToBottom.current = true;
				try { await chatService.markAsRead(conversation._id); } catch (e) { }
			} catch (e) { }
		})();
		return () => {
			cancel = true;
			leaveConversation(conversation._id);
		};
	}, [conversation?._id]);

	// Auto scroll xuống đáy khi có tin mới
	useEffect(() => {
		if (shouldScrollToBottom.current && scrollRef.current) {
			const el = scrollRef.current;
			requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
		}
		shouldScrollToBottom.current = true;
	}, [messages]);

	// Socket listener cho tin nhắn mới
	useEffect(() => {
		if (!socket || !conversation?._id) return;
		const handleNew = (message) => {
			if (message.conversation !== conversation._id) return;
			if (processedMsgIds.current.has(message._id)) return;
			processedMsgIds.current.add(message._id);
			if (processedMsgIds.current.size > 200) processedMsgIds.current.clear();
			setMessages((prev) => {
				if (prev.some((m) => m._id === message._id)) return prev;
				return [...prev, message];
			});
			// Đánh dấu đã đọc nếu cửa sổ đang mở
			const senderId = message.sender?._id?.toString() || message.sender?.toString();
			if (senderId !== currentUserId) {
				chatService.markAsRead(conversation._id).catch(() => { });
			}
		};
		socket.on('message:new', handleNew);
		return () => socket.off('message:new', handleNew);
	}, [socket, conversation?._id, currentUserId]);

	// Khi admin xoá đoạn chat → đóng cửa sổ ngay
	useEffect(() => {
		if (!socket || !conversation?._id) return;
		const handleDeleted = (payload) => {
			if (payload?.conversationId === conversation._id) {
				if (typeof onClose === 'function') onClose();
			}
		};
		const handleRecalled = (payload) => {
			if (payload?.conversationId !== conversation._id) return;
			setMessages((prev) => prev.filter((m) => m._id !== payload.messageId));
		};
		socket.on('conversation:deleted', handleDeleted);
		socket.on('message:recalled', handleRecalled);
		return () => {
			socket.off('conversation:deleted', handleDeleted);
			socket.off('message:recalled', handleRecalled);
		};
	}, [socket, conversation?._id, onClose]);

	const handleLoadMore = useCallback(async () => {
		if (loadingMore || !hasMore) return;
		const firstMsg = messages[0];
		if (!firstMsg) return;
		setLoadingMore(true);
		try {
			const res = await chatService.getMessages(conversation._id, {
				before: firstMsg._id,
				limit: 20,
			});
			const older = res.data || [];
			const container = scrollRef.current;
			const prevHeight = container ? container.scrollHeight : 0;
			shouldScrollToBottom.current = false;
			setMessages((prev) => [...older, ...prev]);
			setHasMore(res.hasMore || false);
			if (container) {
				requestAnimationFrame(() => {
					container.scrollTop = container.scrollHeight - prevHeight;
				});
			}
		} catch (e) { }
		finally { setLoadingMore(false); }
	}, [loadingMore, hasMore, messages, conversation?._id]);

	const handleSend = async () => {
		if (sending) return;
		let file = getSelectedFile();
		const content = input.trim();
		if (!content && !file) return;
		setInput('');
		const replyTo = replyingTo;
		setReplyingTo(null);
		setSending(true);
		try {
			let payload;
			if (file) {
				file = await convertHeicIfNeeded(file);
				payload = new FormData();
				payload.append('file', file);
				if (content) payload.append('content', content);
				if (replyTo) payload.append('replyTo', replyTo._id);
			} else {
				payload = { content };
				if (replyTo) payload.replyTo = replyTo._id;
			}
			if (fileInputRef.current) fileInputRef.current.value = '';
			if (imageInputRef.current) imageInputRef.current.value = '';
			const res = await chatService.sendMessage(conversation._id, payload);
			const msg = res.data;
			if (msg) {
				setMessages((prev) => (prev.some((m) => m._id === msg._id) ? prev : [...prev, msg]));
			}
		} catch (e) {
			setInput(content);
		} finally {
			setSending(false);
		}
	};

	let lastDateLabel = null;

	return (
		<Portal>
		<Paper
			elevation={ 6 }
			sx={ {
				position: 'fixed',
				bottom: { xs: 0, sm: 0 },
				right: { xs: 0, sm: 24 },
				left: { xs: 0, sm: 'auto' },
				top: { xs: 0, sm: 'auto' },
				width: { xs: '100%', sm: 340 },
				height: { xs: '100%', sm: 480 },
				zIndex: 1400,
				display: 'flex',
				flexDirection: 'column',
				borderRadius: { xs: 0, sm: '2px 2px 0 0' },
				overflow: 'hidden',
				bgcolor: '#12131C',
			} }
		>
			{/* Header */}
		<Box sx={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(212,175,55,0.25)', color: '#fff' }}>
			<Box component="img" src={CHAT_HEADER_IMG} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(3px) brightness(0.45)', transform: 'scale(1.06)', pointerEvents: 'none' }} />
			<Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(11,12,16,0.62) 0%, rgba(14,20,40,0.68) 100%)', pointerEvents: 'none' }} />
			<Box sx={{ position: 'relative', zIndex: 1, px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box sx={ { position: 'relative', flexShrink: 0 } }>
					<Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 30, height: 30, fontSize: 12 } }>
						{ other?.fullName?.charAt(0) || other?.username?.charAt(0) }
					</Avatar>
					{ other?.isOnline && (
						<Box sx={ {
							position: 'absolute', bottom: 0, right: 0,
							width: 9, height: 9, borderRadius: '50%',
							bgcolor: '#44b700', border: '2px solid', borderColor: '#1a1a2e',
						} } />
					) }
				</Box>
				<Box sx={ { flex: 1, minWidth: 0 } }>
					<Typography fontWeight={ 700 } fontSize={ 13 } noWrap>
						{ other?.fullName || other?.username }
					</Typography>
					{ other?.isOnline ? (
						<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
							<Box sx={ { width: 7, height: 7, borderRadius: '50%', bgcolor: '#44b700' } } />
							<Typography fontSize={ 10 } sx={ { color: 'rgba(255,255,255,0.85)', lineHeight: 1 } }>
								Đang hoạt động
							</Typography>
						</Box>
					) : (
						<Typography fontSize={ 10 } sx={ { color: 'rgba(255,255,255,0.7)', lineHeight: 1 } }>
							Admin
						</Typography>
					) }
				</Box>
				<IconButton size="small" onClick={ onClose } sx={ { color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } } }>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>
		</Box>

			{/* Messages */ }
			<Box
				ref={ scrollRef }
				onScroll={ (e) => {
					if (e.target.scrollTop === 0 && hasMore && !loadingMore) handleLoadMore();
				} }
				sx={ {
					flex: 1, overflowY: 'auto', p: 1.5,
					display: 'flex', flexDirection: 'column', gap: 0.6,
					background: chatBgColor,
					overscrollBehavior: 'contain',
					WebkitOverflowScrolling: 'touch',
				} }
			>
				{ loadingMore && (
					<Box sx={ { display: 'flex', justifyContent: 'center', py: 0.5 } }>
						<CircularProgress size={ 18 } />
					</Box>
				) }
				<Box sx={ { flex: 1 } } />
				{ messages.length === 0 && !loadingMore && (
					<Box sx={ {
						display: 'flex', flexDirection: 'column', alignItems: 'center',
						justifyContent: 'center', flex: 1, gap: 1, py: 3,
					} }>
						<Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 56, height: 56, fontSize: 22 } }>
							{ other?.fullName?.charAt(0) || other?.username?.charAt(0) }
						</Avatar>
						<Typography fontWeight={ 700 } fontSize={ 14 }>
							{ other?.fullName || other?.username }
						</Typography>
						<Typography fontSize={ 12 } color="text.secondary" textAlign="center" sx={ { px: 2 } }>
							Hãy gửi tin nhắn đầu tiên để bắt đầu cuộc trò chuyện!
						</Typography>
					</Box>
				) }
				{ messages.filter(m => !m.recalled).map((msg, idx) => {
					if (msg.type === 'system') {
						return (
							<Box key={ msg._id || idx } sx={ { display: 'flex', justifyContent: 'center', my: 0.5 } }>
								<Typography sx={ {
									fontSize: 11, color: '#888', bgcolor: otherBubbleColor,
									px: 1.5, py: 0.4, borderRadius: 3, textAlign: 'center', maxWidth: '85%',
								} }>
									{ msg.content }
								</Typography>
							</Box>
						);
					}
					const isOwn = msg.sender?._id?.toString() === currentUserId
						|| msg.sender?.toString() === currentUserId;
					const dateLabel = msg.createdAt ? formatDateLabel(msg.createdAt) : null;
					const showDateSep = dateLabel && dateLabel !== lastDateLabel;
					if (showDateSep) lastDateLabel = dateLabel;
					const att = msg.attachments?.length > 0 ? msg.attachments[0] : null;

					return (
						<React.Fragment key={ msg._id || idx }>
							{ showDateSep && (
								<Box sx={ { display: 'flex', justifyContent: 'center', my: 0.5 } }>
									<Typography sx={ {
										fontSize: 11, color: 'rgba(255,255,255,0.6)', bgcolor: datePillColor,
										px: 1.5, py: 0.25, borderRadius: 3,
									} }>
										{ dateLabel }
									</Typography>
								</Box>
							) }
							<Box
								sx={ {
									display: 'flex',
									justifyContent: isOwn ? 'flex-end' : 'flex-start',
									alignItems: 'center', gap: 0.5,
								} }
								onMouseEnter={ (e) => {
									e.currentTarget.querySelectorAll('.msg-action-btn').forEach((b) => (b.style.opacity = '1'));
								} }
								onMouseLeave={ (e) => {
									e.currentTarget.querySelectorAll('.msg-action-btn').forEach((b) => (b.style.opacity = '0'));
								} }
							>
								{ !isOwn && (
									<Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 22, height: 22, fontSize: 10 } }>
										{ other?.fullName?.charAt(0) }
									</Avatar>
								) }
								{ isOwn && (
								<IconButton
									className="msg-action-btn"
									size="small"
									onClick={ () => setReplyingTo(msg) }
									sx={ {
										opacity: 0, transition: 'opacity 0.15s', p: 0.5,
										color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)' },
									} }
								>
									<ReplyIcon sx={ { fontSize: 18 } } />
								</IconButton>
								) }
								<Box sx={ {
									display: 'flex', flexDirection: 'column',
									alignItems: isOwn ? 'flex-end' : 'flex-start',
									gap: 0.2, maxWidth: '72%',
								} }>
									{ msg.replyTo && (
										<Box sx={ {
											bgcolor: otherBubbleColor, borderLeft: '3px solid #bbb',
											px: 1, py: 0.5, borderRadius: 1, fontSize: 11,
											maxWidth: '100%', overflow: 'hidden',
										} }>
											<Typography sx={ { fontSize: 10, fontWeight: 600, color: '#888' } }>
												{ msg.replyTo.sender?.fullName || 'Tin nhắn' }
											</Typography>
											{ msg.replyTo.recalled ? (
												<Typography sx={ { fontSize: 11, color: '#aaa', fontStyle: 'italic' } }>
													Tin nhắn đã được thu hồi
												</Typography>
											) : (() => {
												const rAtt = msg.replyTo.attachments?.[0];
												return rAtt?.type === 'image' ? (
													<Box component="img" src={ `${ CHAT_API_BASE }${ rAtt.url }` } alt={ rAtt.name }
														onClick={ (e) => { e.stopPropagation(); setImagePreview(`${ CHAT_API_BASE }${ rAtt.url }`); } }
														sx={ { maxHeight: 60, maxWidth: 100, borderRadius: 1, display: 'block', mt: 0.3, cursor: 'zoom-in' } } />
												) : rAtt?.type === 'file' ? (
													<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
														<AttachFileIcon sx={ { fontSize: 11, color: 'text.secondary' } } />
														<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>
															{ rAtt.name }
														</Typography>
													</Box>
												) : (
													<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>
														{ msg.replyTo.content }
													</Typography>
												);
											})() }
										</Box>
									) }
									{ att?.type === 'image' ? (
										<Box component="img" src={ `${ CHAT_API_BASE }${ att.url }` } alt={ att.name }
											onClick={ () => setImagePreview(`${ CHAT_API_BASE }${ att.url }`) }
											sx={ { maxWidth: 210, borderRadius: 2, display: 'block', cursor: 'zoom-in' } } />
									) : (
										<Box sx={ {
											bgcolor: isOwn ? ownBubbleColor : otherBubbleColor,
											color: isOwn ? (ownBubbleColor === '#D4AF37' ? '#0B0C10' : '#fff') : '#E5E5E5',
											px: 1.5, py: 0.75,
											borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
											fontSize: 13.5, lineHeight: 1.4, wordBreak: 'break-word',
										} }>
											{ att?.type === 'file' ? (
												<Box component="a" href={ `${ CHAT_API_BASE }${ att.url }` } target="_blank" rel="noreferrer"
													sx={ { display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit', textDecoration: 'none' } }>
													<AttachFileIcon sx={ { fontSize: 14 } } />
													<Typography sx={ { fontSize: 12, textDecoration: 'underline' } }>{ att.name }</Typography>
												</Box>
											) : msg.content }
										</Box>
									) }
									{ msg.createdAt && (
										<Typography sx={ { fontSize: 10, color: '#aaa', px: 0.5 } }>
											{ formatTime(msg.createdAt) }
										</Typography>
									) }
								</Box>
								{ !isOwn && (
								<IconButton
									className="msg-action-btn"
									size="small"
									onClick={ () => setReplyingTo(msg) }
									sx={ {
										opacity: 0, transition: 'opacity 0.15s', p: 0.5,
										color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)' },
									} }
								>
									<ReplyIcon sx={ { fontSize: 18 } } />
								</IconButton>
								) }
							</Box>
						</React.Fragment>
					);
				}) }
				<div ref={ endRef } />
			</Box>

			{/* Reply bar */ }
			{ replyingTo && (
				<Box sx={ {
					px: 1.5, py: 0.75, bgcolor: '#0e0f17',
					borderTop: '1px solid rgba(255,255,255,0.1)',
					display: 'flex', alignItems: 'center', gap: 1,
				} }>
					<ReplyIcon sx={ { fontSize: 14, color: 'rgba(255,255,255,0.45)' } } />
					<Box sx={ { flex: 1, minWidth: 0 } }>
						<Typography sx={ { fontSize: 10, fontWeight: 600, color: 'rgba(212,175,55,0.85)' } }>
							{ replyingTo.sender?.fullName || 'Tin nhắn' }
						</Typography>
						<Typography sx={ {
							fontSize: 11, color: 'rgba(255,255,255,0.5)',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						} }>
							{ replyingTo.attachments?.[0]?.name || replyingTo.content }
						</Typography>
					</Box>
					<IconButton size="small" onClick={ () => setReplyingTo(null) } sx={ { p: 0.3 } }>
						<CloseIcon sx={ { fontSize: 14 } } />
					</IconButton>
				</Box>
			) }

			{/* Input */ }
			<Box sx={ {
				px: 1.5, py: 1, borderTop: '1px solid rgba(255,255,255,0.1)',
				display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#12131C',
			} }>
				<input
					ref={ imageInputRef }
					type="file"
					style={ { position: 'fixed', top: '-100vh', left: '-100vw', width: '1px', height: '1px', opacity: 0 } }
					onChange={ () => { const f = imageInputRef.current?.files?.[0]; console.log('[FloatingChat] Ảnh chọn:', f ? { name: f.name, size: f.size, type: f.type } : null); if (f) handleSend(); } }
				/>
				<input
					ref={ fileInputRef }
					type="file"
					style={ { position: 'fixed', top: '-100vh', left: '-100vw', width: '1px', height: '1px', opacity: 0 } }
					onChange={ () => { const f = fileInputRef.current?.files?.[0]; console.log('[FloatingChat] File chọn:', f ? { name: f.name, size: f.size, type: f.type } : null); if (f) handleSend(); } }
				/>
				<IconButton
					size="small"
					onClick={ () => { imageInputRef.current?.click(); } }
					sx={ { color: '#D4AF37', p: 0.5, '&:hover': { bgcolor: 'rgba(212,175,55,0.12)' } } }
				>
					<ImageIcon sx={ { fontSize: 18 } } />
				</IconButton>
				<IconButton
					size="small"
					onClick={ () => { fileInputRef.current?.click(); } }
					sx={ { color: '#D4AF37', p: 0.5, '&:hover': { bgcolor: 'rgba(212,175,55,0.12)' } } }
				>
					<AttachFileIcon sx={ { fontSize: 18 } } />
				</IconButton>
				<InputBase
					placeholder="Aa"
					value={ input }
					onChange={ (e) => setInput(e.target.value) }
					onKeyDown={ (e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							handleSend();
						}
					} }
					sx={ {
						flex: 1, fontSize: 15, bgcolor: 'rgba(255,255,255,0.06)',
						borderRadius: '2px', px: 1.5, py: 0.5, color: '#E5E5E5',
					} }
					multiline
					maxRows={ 3 }
				/>
				<IconButton
					size="small"
					onClick={ handleSend }
					disabled={ (!input.trim() && !getSelectedFile()) || sending }
					sx={ { color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212,175,55,0.12)' } } }
				>
					<SendIcon fontSize="small" />
				</IconButton>
			</Box>
			<ImagePreviewModal
				open={ !!imagePreview }
				src={ imagePreview }
				onClose={ () => setImagePreview(null) }
			/>
		</Paper>
		</Portal>
	);
};

export default FloatingChatWindow;
